import { useEffect, useMemo, useRef, useState } from 'react';

import { useParams } from 'react-router-dom';

import { Background } from '@appcharge/shared-ui';
import {
  Checkbox,
  FormControlLabel,
  Grid,
  Typography
} from '@mui/material';
import useFeatureFlags from 'api/useFeatureFlags';
import useImages from 'api/useImages';
import useIntegration from 'api/useIntegration';
import useTheme from 'api/useTheme';
import {
  EAssetType,
  EFeatureFlag,
  EFontWeight,
  ELogoSize,
  EStyleProperty
} from 'constants/enums';
import { useFormikContext } from 'formik';
import { UploadsTooltips, useUploads } from 'hooks/useUpload';
import { LoginPreview } from 'Previews/LoginPage';
import { getStyledSelectItem } from 'utils/getStyledSelectItem';
import { permissionsUtil } from 'utils/permissionsUtil';
import { inputFontOptions } from 'views/Builder/constants';

import { AcAdvancedTextInput } from 'components/AcAdvancedTextInput/AcAdvancedInput';
import AcCard from 'components/AcCard/AcCard';
import AcGradientInput from 'components/AcGradientInput/AcGradientInput';
import AcInput from 'components/AcInput/AcInput';
import AcSelect from 'components/AcSelect/AcSelect';
import AcSolidInput from 'components/AcSolidInput/AcSolidInput';
import Input from 'components/Input/Input';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import { ColorPicker } from 'components/ui/ColorPicker/color-picker';
import { Flex } from 'components/ui/flex';
import { Separator } from 'components/ui/separator';
import UploadImage from 'components/UploadImage/UploadImage';

import { ThemeLoader } from '../ThemeLoader';
import { getFonts } from '../utils';

import { EFacebookButtonVariant, LoginThemeFormValues } from './types';

import 'style/forms.scss';

const LOGOUT_WEIGHT_OPTIONS = [
  { value: '300', label: 'Regular' },
  { value: '500', label: 'Medium' },
  { value: '700', label: 'Bold' }
];

export const LoginTheme: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { values, handleBlur, handleChange, setFieldValue, errors, touched } =
    useFormikContext<LoginThemeFormValues>();
  const { publisherId } = useParams();
  const pictures = useImages(publisherId).getImages;
  const addPictureInputRef = useRef<HTMLInputElement>(null);
  const { getIntegration } = useIntegration(publisherId);
  const [currentUploadingField, setCurrentUploadingField] = useState('');
  const { uploadImage } = useUploads();
  const { featureFlags } = useFeatureFlags({ publisherId });

  const { getGeneralTheme: generalTheme, getLoginTheme: loginTheme } =
    useTheme(publisherId);

  const showNewPrivacySection: boolean = useMemo(
    () => featureFlags?.[EFeatureFlag.DASHBOARD_NEW_PRIVACY_RICH_TEXT],
    [featureFlags]
  );

  const showFacebookButtonConfig: boolean = useMemo(
    () => featureFlags?.[EFeatureFlag.DASHBOARD_FB_BUTTON_CONFIG],
    [featureFlags]
  );

  const hasValues = useMemo(() => Object.keys(values).length > 0, [values]);

  useEffect(
    () => setIsLoading(loginTheme.isLoading || !loginTheme.data || !hasValues),
    [loginTheme?.isLoading, loginTheme?.data, hasValues]
  );

  if (isLoading) {
    return <ThemeLoader />;
  }

  return (
    <Grid container className="formContent">
      <Grid item xs={5}>
        <Typography
          variant="h6"
          sx={{
            marginBottom: '12px',
            fontFamily: 'Inter, Montserrat, serif',
            fontWeight: '600'
          }}
        >
          Login Page
        </Typography>
        <Flex>
          <AcCard
            stackContainer={false}
            title="Now Say 'Hi'"
            description="Greet your players with a welcome message. This will be your players’ first encounter with your store, so be nice!"
          >
            <Grid container rowSpacing={2} columnSpacing={1.5}>
              <Grid item xs={12}>
                <AcInput
                  header="Text"
                  name="text"
                  value={values.text}
                  tooltip="This will be the text that welcomes your users"
                  type="text"
                  dataTestId="loginText"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.text && Boolean(errors.text)}
                />
              </Grid>
              <Grid item xs={4}>
                <AcSolidInput
                  header="Color"
                  name="textColor"
                  defaultValue={values.textColor}
                  setValue={setFieldValue}
                  onBlur={handleBlur}
                  dataTestId="loginTextColor"
                  dataTestPopoverId="loginTextColorPopover"
                  dataTestColorPickerId="loginTextColorPicker"
                  error={touched.textColor && Boolean(errors.textColor)}
                />
              </Grid>
              <Grid item xs={4}>
                <AcInput
                  header="Size (px)"
                  name="textSize"
                  type="number"
                  dataTestId="loginTextSize"
                  slotProps={{
                    htmlInput: {
                      max: 32,
                      min: 16
                    }
                  }}
                  value={values.textSize}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.textSize && Boolean(errors.textSize)}
                />
              </Grid>
              <Grid item xs={4}>
                <AcSelect
                  header="Weight"
                  name="textWeight"
                  defaultValue={EFontWeight.Regular}
                  value={values.textWeight}
                  dataTestBoxId="loginTextWeight"
                  dataTestListId="loginTextWeightList"
                  optionDataTestIdPrefix="loginTextWeightOption"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.textWeight && Boolean(errors.textWeight)}
                  items={[
                    {
                      content: 'Regular',
                      key: EFontWeight.Regular,
                      value: '300',
                      renderFunction: () =>
                        getStyledSelectItem(
                          EFontWeight.Regular,
                          EStyleProperty.fontWeight
                        )
                    },
                    {
                      content: 'Medium',
                      key: EFontWeight.Medium,
                      value: '500',
                      renderFunction: () =>
                        getStyledSelectItem(
                          EFontWeight.Medium,
                          EStyleProperty.fontWeight
                        )
                    },
                    {
                      content: 'Bold',
                      key: EFontWeight.Bold,
                      value: '700',
                      renderFunction: () =>
                        getStyledSelectItem(
                          EFontWeight.Bold,
                          EStyleProperty.fontWeight
                        )
                    }
                  ]}
                />
              </Grid>
            </Grid>
          </AcCard>
          <Separator className="my-6 bg-base-border" />
          <AcCard stackContainer={false} title="Font">
            <Grid container rowSpacing={2} columnSpacing={1.5}>
              <Grid item xs={12}>
                <AcSelect
                  header="Font"
                  name="font"
                  value={values.font}
                  dataTestBoxId="loginFont"
                  dataTestListId="loginFontList"
                  optionDataTestIdPrefix="loginFontOption"
                  items={getFonts()}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.font && Boolean(errors.font)}
                />
              </Grid>
            </Grid>
          </AcCard>
          <Separator className="my-6 bg-base-border" />
          <AcCard
            stackContainer={false}
            title="Error Message"
            description="Control your fallback error messages"
          >
            <Grid container rowSpacing={2} columnSpacing={1.5}>
              <Grid item xs={12}>
                <Grid item xs={6}>
                  <AcInput
                    headerSize="14"
                    header="Title"
                    name="errorMessage.title"
                    value={values.errorMessage.title}
                    dataTestId="errorMessageTitle"
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>
                <Grid item xs={6}>
                  <AcInput
                    headerSize="14"
                    header="Subtitle"
                    name="errorMessage.subTitle"
                    value={values.errorMessage.subTitle}
                    dataTestId="errorMessageSubtitle"
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>
              </Grid>
            </Grid>
          </AcCard>
          <Separator className="my-6 bg-base-border" />
          <AcCard stackContainer={false} title="Logout Text Settings">
            <div className="mt-7 flex flex-col gap-[21px]">
              <UploadImage
                title="Button Image"
                description="For optimized display, make sure your image is at least 2:1 ratio | PNG, JPEG | Max 40px height"
                buttonText="Upload"
                className="w-full"
                forceType={EAssetType.LOGO}
                selectedImageUrl={values.logoutScreen.buttonImage}
                data-testid="logoutScreenButtonImage"
                buttonDataTestId="uploadLogoutScreenButtonImage"
                onUpload={(url: string) =>
                  setFieldValue('logoutScreen.buttonImage', url)
                }
              />
              <UploadImage
                title="Custom 'X' Icon"
                description="Upload an image to replace the default exit icon | 1:1 Ratio"
                buttonText="Upload"
                className="w-full"
                forceType={EAssetType.LOGO}
                selectedImageUrl={values.logoutScreen.customCloseIcon}
                data-testid="logoutScreenCustomCloseIcon"
                buttonDataTestId="uploadLogoutScreenCustomCloseIcon"
                onUpload={(url: string) =>
                  setFieldValue('logoutScreen.customCloseIcon', url)
                }
              />
              <Input
                label="Text"
                placeholder="Are you sure you want to disconnect?"
                name="logoutScreen.text"
                value={values.logoutScreen.text}
                onChange={handleChange}
                onBlur={handleBlur}
                data-testid="logoutScreenText"
                inputClassName="focus:ring-inset"
              />
              <div className="grid grid-cols-2 gap-4">
                <InputSelectDropdown
                  label="Font"
                  placeholder="Select"
                  buttonDataTestId="logoutScreenFontSelect"
                  listDataTestId="logoutScreenFontList"
                  options={inputFontOptions}
                  defaultSelected={inputFontOptions.find(
                    (option) => option.value === values.logoutScreen.font
                  )}
                  onOptionChange={(value: string) =>
                    setFieldValue('logoutScreen.font', value)
                  }
                  onClear={() => setFieldValue('logoutScreen.font', '')}
                  shouldCapitalizeOptions={false}
                />
                <ColorPicker
                  label="Color"
                  data-testid="logoutScreenTextColor"
                  defaultValue={values.logoutScreen.textColor}
                  onChange={(value: string) =>
                    setFieldValue('logoutScreen.textColor', value)
                  }
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Size (px)"
                  type="number"
                  name="logoutScreen.textSize"
                  value={values.logoutScreen.textSize}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  data-testid="logoutScreenTextSize"
                  inputClassName="focus:ring-inset"
                  state={
                    touched.logoutScreen?.textSize &&
                      errors.logoutScreen?.textSize
                      ? 'error'
                      : 'default'
                  }
                  description={
                    touched.logoutScreen?.textSize
                      ? errors.logoutScreen?.textSize?.toString()
                      : ''
                  }
                />
                <InputSelectDropdown
                  label="Weight"
                  placeholder="Select"
                  buttonDataTestId="logoutScreenWeightSelect"
                  listDataTestId="logoutScreenWeightList"
                  options={LOGOUT_WEIGHT_OPTIONS}
                  defaultSelected={LOGOUT_WEIGHT_OPTIONS.find(
                    (option) => option.value === values.logoutScreen.textWeight
                  )}
                  onOptionChange={(value: string) =>
                    setFieldValue('logoutScreen.textWeight', value)
                  }
                />
              </div>
            </div>
          </AcCard>
          <Separator className="my-6 bg-base-border" />
          <AcCard
            stackContainer={false}
            title="Assets"
            description="The brand assets you’ll add here will appear only in this screen. You can change them any time."
          >
            <Grid container rowSpacing={2} columnSpacing={1.5}>
              <Grid item xs={6}>
                <input
                  style={{ display: 'none' }}
                  type="file"
                  onChange={(e) =>
                    uploadImage(
                      e,
                      addPictureInputRef.current as HTMLInputElement,
                      pictures,
                      setFieldValue,
                      setCurrentUploadingField,
                      currentUploadingField
                    )
                  }
                  ref={addPictureInputRef}
                  accept="image/*"
                />
                <AcSelect
                  header="Logo"
                  name="loginLogoImage"
                  value={values.loginLogoImage || generalTheme?.data?.logo}
                  dataTestBoxId="loginLogoImage"
                  uploadBtnTestId="uploadLoginLogoImage"
                  dataTestListId="loginLogoImageList"
                  optionDataTestIdPrefix="loginLogoImageOption"
                  uploadConfig={{
                    onUploadSuccess: async (url: string) => {
                      await pictures.refetch();
                      setFieldValue('loginLogoImage', url);
                    },
                    uploadType: EAssetType.LOGO,
                    uploadMessage: UploadsTooltips[EAssetType.LOGO]
                  }}
                  items={[
                    {
                      content: 'Default Appcharge logo',
                      key: 'https://media.appcharge.com/defaults/default-store-logo.svg',
                      value:
                        'https://media.appcharge.com/defaults/default-store-logo.svg',
                      renderFunction: () => {
                        return (
                          <Flex direction="row" align="center" className="gap-2">
                            <img
                              src="https://media.appcharge.com/defaults/default-store-logo.svg"
                              alt="Default Appcharge logo"
                              style={{
                                maxWidth: 26
                              }}
                            />
                            <Typography>Default Appcharge logo</Typography>
                          </Flex>
                        );
                      }
                    },
                    ...(pictures.data
                      ? pictures.data
                        .filter((p: any) => p.type === EAssetType.LOGO)
                        .map((picture: any) => {
                          return {
                            content: picture.name,
                            key: picture.url,
                            value: picture.url,
                            renderFunction: () => {
                              return (
                                <Flex
                                  direction="row"
                                  align="center"
                                  className="gap-2"
                                >
                                  <img
                                    src={picture.url}
                                    alt={picture.name}
                                    style={{
                                      width: 26,
                                      maxHeight: 30
                                    }}
                                  />
                                  <Typography>{picture.name}</Typography>
                                </Flex>
                              );
                            }
                          };
                        })
                      : [])
                  ]}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    touched.loginLogoImage && Boolean(errors.loginLogoImage)
                  }
                  tooltip="This will be the logo of your login page."
                />
              </Grid>
              <Grid item xs={6}>
                <AcSelect
                  header="Logo Size"
                  name="loginLogoSize"
                  value={values.loginLogoSize}
                  dataTestBoxId="loginLogoSize"
                  dataTestListId="loginLogoSizeList"
                  optionDataTestIdPrefix="loginLogoSizeOption"
                  items={[
                    {
                      content: 'Small',
                      key: ELogoSize.SMALL,
                      value: ELogoSize.SMALL
                    },
                    {
                      content: 'Large',
                      key: ELogoSize.BIG,
                      value: ELogoSize.BIG
                    }
                  ]}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.loginLogoSize && Boolean(errors.loginLogoSize)}
                  tooltip="Image with 1:1 ratio recommended, with at least 200px size for small view and 400px size for large view"
                />
              </Grid>
              <Grid item xs={12}>
                <AcSelect
                  header="Login Background Image (Mobile)"
                  name="loginBackgroundImageMobile"
                  tooltip="If no image is specified, the store's background will be used"
                  value={values.loginBackgroundImageMobile}
                  dataTestBoxId="loginBackgroundImageMobile"
                  uploadBtnTestId="uploadLoginBackgroundImageMobile"
                  dataTestListId="loginBackgroundImageMobileList"
                  optionDataTestIdPrefix="loginBackgroundImageMobileOption"
                  uploadConfig={{
                    onUploadSuccess: async (url: string) => {
                      await pictures.refetch();
                      setFieldValue('loginBackgroundImageMobile', url);
                    },
                    uploadType: EAssetType.BG_MOBILE,
                    uploadMessage: UploadsTooltips[EAssetType.BG_MOBILE]
                  }}
                  items={[
                    {
                      content: 'No image',
                      key: 'no-image',
                      value: '',
                      renderFunction: () => (
                        <Flex direction="row" align="center" className="gap-2">
                          <span
                            style={{
                              width: 30,
                              height: 30,
                              display: 'inline-block',
                              background: '#eee',
                              textAlign: 'center',
                              lineHeight: '30px'
                            }}
                          ></span>
                          <Typography>No Image</Typography>
                        </Flex>
                      )
                    },
                    {
                      content: 'Default Appcharge background',
                      key: 'https://media.appcharge.com/defaults/background.png',
                      value:
                        'https://media.appcharge.com/defaults/background.png',
                      renderFunction: () => {
                        return (
                          <Flex direction="row" align="center" className="gap-2">
                            <img
                              src="https://media.appcharge.com/defaults/background.png"
                              alt="Default Appcharge background"
                              style={{
                                width: 30,
                                maxHeight: 30
                              }}
                            />
                            <Typography>
                              Default Appcharge background
                            </Typography>
                          </Flex>
                        );
                      }
                    },
                    ...(pictures.data
                      ? pictures.data
                        .filter((p: any) => p.type === EAssetType.BG_MOBILE)
                        .map((picture: any) => {
                          return {
                            content: picture.name,
                            key: picture.url,
                            value: picture.url,
                            renderFunction: () => {
                              return (
                                <Flex
                                  direction="row"
                                  align="center"
                                  className="gap-2"
                                >
                                  <img
                                    src={picture.url}
                                    alt={picture.name}
                                    style={{
                                      width: 30,
                                      maxHeight: 30
                                    }}
                                  />
                                  <Typography>{picture.name}</Typography>
                                </Flex>
                              );
                            }
                          };
                        })
                      : [])
                  ]}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    touched.loginBackgroundImageMobile &&
                    Boolean(errors.loginBackgroundImageMobile)
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <AcSelect
                  header="Login Background Image (Desktop)"
                  name="loginBackgroundImageDesktop"
                  tooltip="If no image is specified, the store's background will be used"
                  value={values.loginBackgroundImageDesktop}
                  dataTestBoxId="loginBackgroundImageDesktop"
                  uploadBtnTestId="uploadLoginBackgroundImageDesktop"
                  dataTestListId="loginBackgroundImageDesktopList"
                  optionDataTestIdPrefix="loginBackgroundImageDesktopOption"
                  uploadConfig={{
                    onUploadSuccess: async (url: string) => {
                      await pictures.refetch();
                      setFieldValue('loginBackgroundImageDesktop', url);
                    },
                    uploadType: EAssetType.BG_DESKTOP,
                    uploadMessage: UploadsTooltips[EAssetType.BG_DESKTOP]
                  }}
                  items={[
                    {
                      content: 'No image',
                      key: 'no-image',
                      value: '',
                      renderFunction: () => (
                        <Flex direction="row" align="center" className="gap-2">
                          <span
                            style={{
                              width: 30,
                              height: 30,
                              display: 'inline-block',
                              background: '#eee',
                              textAlign: 'center',
                              lineHeight: '30px'
                            }}
                          ></span>
                          <Typography>No Image</Typography>
                        </Flex>
                      )
                    },
                    {
                      content: 'Default Appcharge background',
                      key: 'https://media.appcharge.com/defaults/background.png',
                      value:
                        'https://media.appcharge.com/defaults/background.png',
                      renderFunction: () => {
                        return (
                          <Flex direction="row" align="center" className="gap-2">
                            <img
                              src="https://media.appcharge.com/defaults/background.png"
                              alt="Default Appcharge background"
                              style={{
                                width: 30,
                                maxHeight: 30
                              }}
                            />
                            <Typography>
                              Default Appcharge background
                            </Typography>
                          </Flex>
                        );
                      }
                    },
                    ...(pictures.data
                      ? pictures.data
                        .filter((p: any) => p.type === EAssetType.BG_DESKTOP)
                        .map((picture: any) => {
                          return {
                            content: picture.name,
                            key: picture.url,
                            value: picture.url,
                            renderFunction: () => {
                              return (
                                <Flex
                                  direction="row"
                                  align="center"
                                  className="gap-2"
                                >
                                  <img
                                    src={picture.url}
                                    alt={picture.name}
                                    style={{
                                      width: 30,
                                      maxHeight: 30
                                    }}
                                  />
                                  <Typography>{picture.name}</Typography>
                                </Flex>
                              );
                            }
                          };
                        })
                      : [])
                  ]}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    touched.loginBackgroundImageDesktop &&
                    Boolean(errors.loginBackgroundImageDesktop)
                  }
                />
              </Grid>
            </Grid>
          </AcCard>
          {getIntegration.data?.playersAuthentication.otpOn && (
            <>
              <Separator className="my-6 bg-base-border" />
              <AcCard
                stackContainer={false}
                title="OTP"
                description="Control your OTP login button design"
              >
                <Grid container rowSpacing={2} columnSpacing={1.5}>
                  <Grid item xs={12}>
                    <input
                      style={{ display: 'none' }}
                      type="file"
                      onChange={(e) =>
                        uploadImage(
                          e,
                          addPictureInputRef.current as HTMLInputElement,
                          pictures,
                          setFieldValue,
                          setCurrentUploadingField,
                          currentUploadingField
                        )
                      }
                      ref={addPictureInputRef}
                      accept="image/*"
                    />
                    <AcSelect
                      header="OTP Icon"
                      name="otpButton.icon"
                      value={values.otpButton.icon}
                      dataTestBoxId="otpButtonIcon"
                      uploadBtnTestId="uploadOtpButtonIcon"
                      dataTestListId="otpButtonIconList"
                      optionDataTestIdPrefix="otpButtonIconOption"
                      uploadConfig={{
                        onUploadSuccess: async (url: string) => {
                          await pictures.refetch();
                          setFieldValue('otpButton.icon', url);
                        },
                        uploadType: EAssetType.LOGO,
                        uploadMessage: UploadsTooltips[EAssetType.LOGO]
                      }}
                      items={[
                        {
                          content: 'No image',
                          key: 'no-image',
                          value: '',
                          renderFunction: () => (
                            <Flex direction="row" align="center" className="gap-2">
                              <span
                                style={{
                                  width: 30,
                                  height: 30,
                                  display: 'inline-block',
                                  background: '#eee',
                                  textAlign: 'center',
                                  lineHeight: '30px'
                                }}
                              ></span>
                              <Typography>No Image</Typography>
                            </Flex>
                          )
                        },
                        {
                          content: 'Default Appcharge logo',
                          key: 'https://media.appcharge.com/defaults/default-store-logo.svg',
                          value:
                            'https://media.appcharge.com/defaults/default-store-logo.svg',
                          renderFunction: () => {
                            return (
                              <Flex
                                direction="row"
                                align="center"
                                className="gap-2"
                              >
                                <img
                                  src="https://media.appcharge.com/defaults/default-store-logo.svg"
                                  alt="Default Appcharge logo"
                                  style={{
                                    maxWidth: 26
                                  }}
                                />
                                <Typography>Default Appcharge logo</Typography>
                              </Flex>
                            );
                          }
                        },
                        ...(pictures.data
                          ? pictures.data
                            .filter((p: any) => p.type === EAssetType.LOGO)
                            .map((picture: any) => {
                              return {
                                content: picture.name,
                                key: picture.url,
                                value: picture.url,
                                renderFunction: () => {
                                  return (
                                    <Flex
                                      direction="row"
                                      align="center"
                                      className="gap-2"
                                    >
                                      <img
                                        src={picture.url}
                                        alt={picture.name}
                                        style={{
                                          width: 26,
                                          maxHeight: 30
                                        }}
                                      />
                                      <Typography>{picture.name}</Typography>
                                    </Flex>
                                  );
                                }
                              };
                            })
                          : [])
                      ]}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.otpButton?.icon &&
                        Boolean(errors.otpButton?.icon)
                      }
                      tooltip="This will be the icon of your otp login button."
                    />
                  </Grid>
                </Grid>
                <Grid container rowSpacing={2} columnSpacing={1.5} pt={2}>
                  <Grid item xs={12}>
                    <AcInput
                      headerSize="14"
                      header="OTP Button Text"
                      name="otpButton.text"
                      value={values.otpButton.text}
                      dataTestId="otpButtonText"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.otpButton?.text &&
                        Boolean(errors.otpButton?.text)
                      }
                      helperText={
                        touched.otpButton?.text
                          ? errors.otpButton?.text?.toString()
                          : ''
                      }
                    />
                  </Grid>
                </Grid>
                <Grid container rowSpacing={2} columnSpacing={1.5} pt={2}>
                  <Grid item xs={6}>
                    <AcGradientInput
                      header="Button Color"
                      name="otpButton.backgroundColor"
                      defaultValue={values.otpButton?.backgroundColor}
                      setValue={setFieldValue}
                      onBlur={handleBlur}
                      dataTestId="otpButtonBackgroundColor"
                      dataTestPopoverId="otpButtonBackgroundColorPopover"
                      dataTestColorOneId="otpButtonBackgroundColorOne"
                      dataTestColorTwoId="otpButtonBackgroundColorTwo"
                      dataTestGradientTypeId="otpButtonBackgroundColorGradientType"
                      dataTestGradientDirectionId="otpButtonBackgroundColorGradientDirection"
                      error={
                        touched.otpButton?.backgroundColor?.colorOne &&
                        Boolean(errors.otpButton?.backgroundColor?.colorOne)
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <AcGradientInput
                      header="Text Color"
                      name="otpButton.textColor"
                      defaultValue={values.otpButton?.textColor}
                      setValue={setFieldValue}
                      onBlur={handleBlur}
                      dataTestId="otpButtonTextColor"
                      dataTestPopoverId="otpButtonTextColorPopover"
                      dataTestColorOneId="otpButtonTextColorOne"
                      dataTestColorTwoId="otpButtonTextColorTwo"
                      dataTestGradientTypeId="otpButtonTextColorGradientType"
                      dataTestGradientDirectionId="otpButtonTextColorGradientDirection"
                      error={
                        touched.otpButton?.textColor &&
                        Boolean(errors.otpButton?.textColor)
                      }
                      helperText={
                        touched.otpButton?.textColor
                          ? errors.otpButton?.textColor?.toString()
                          : ''
                      }
                    />
                  </Grid>
                </Grid>
              </AcCard>
            </>
          )}
          {showFacebookButtonConfig && (
            <>
              <Separator className="my-6 bg-base-border" />
              <AcCard
                stackContainer={false}
                title="Facebook Button"
                description="Configure the Facebook login button appearance"
              >
                <Grid container rowSpacing={2} columnSpacing={1.5}>
                  <Grid item xs={12}>
                    <AcSelect
                      header="Button Variant"
                      name="facebookButton.variant"
                      value={values.facebookButton?.variant}
                      dataTestBoxId="facebookButtonVariant"
                      dataTestListId="facebookButtonVariantList"
                      optionDataTestIdPrefix="facebookButtonVariantOption"
                      items={[
                        {
                          content: 'Default',
                          key: EFacebookButtonVariant.DEFAULT,
                          value: EFacebookButtonVariant.DEFAULT
                        },
                        {
                          content: 'Outlined',
                          key: EFacebookButtonVariant.OUTLINED,
                          value: EFacebookButtonVariant.OUTLINED
                        }
                      ]}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.facebookButton?.variant &&
                        Boolean(errors.facebookButton?.variant)
                      }
                      tooltip="Choose the visual style of the Facebook login button"
                    />
                  </Grid>
                </Grid>
              </AcCard>
            </>
          )}
          {showNewPrivacySection && (
            <>
              <Separator className="my-6 bg-base-border" />
              <AcCard stackContainer={false} title="Disclaimer">
                <Grid container rowSpacing={2} columnSpacing={1.5}>
                  <Grid item xs={12}>
                    <Grid item xs={12}>
                      <AcAdvancedTextInput
                        name="consentSection.htmlText"
                        value={values.consentSection?.htmlText}
                        setValue={(value) =>
                          setFieldValue('consentSection.htmlText', value)
                        }
                        error={
                          touched.consentSection?.htmlText &&
                          Boolean(errors.consentSection?.htmlText)
                        }
                      />
                    </Grid>
                    <Flex direction="row" justify="between" align="center">
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="setAsFree"
                            checked={values.consentSection?.consentRequired}
                            onBlur={handleBlur}
                            onChange={async (e: any) => {
                              const isChecked = e.target.checked;
                              await setFieldValue(
                                'consentSection.consentRequired',
                                isChecked
                              );
                            }}
                            inputProps={
                              {
                                'data-testid': 'consentSectionConsentRequired'
                              } as React.InputHTMLAttributes<HTMLInputElement>
                            }
                          />
                        }
                        label="Ask for player's active consent before enabling the
                        login methods"
                      />
                    </Flex>
                  </Grid>
                </Grid>
              </AcCard>
            </>
          )}
          <Separator className="my-6 bg-base-border" />
          <AcCard stackContainer={false} title="Player ID">
            <div className="mt-7 flex flex-col gap-[21px]">
              <UploadImage
                title="Player ID Icon"
                buttonText="Upload"
                variant="storeSettings"
                className="w-full"
                hideInfoIcon
                forceType={EAssetType.LOGO}
                selectedImageUrl={values.playerIdButton.icon}
                data-testid="playerIdButtonIcon"
                buttonDataTestId="uploadPlayerIdButtonIcon"
                onUpload={(url: string) =>
                  setFieldValue('playerIdButton.icon', url)
                }
              />
              <Input
                label="Player ID Button Text"
                placeholder="Enter Text"
                name="playerIdButton.text"
                value={values.playerIdButton.text}
                onChange={handleChange}
                onBlur={handleBlur}
                data-testid="playerIdButtonText"
                inputClassName="focus:ring-inset"
              />
              <div className="grid grid-cols-2 gap-4">
                <AcGradientInput
                  header="Button Color"
                  name="playerIdButton.backgroundColor"
                  defaultValue={values.playerIdButton?.backgroundColor}
                  setValue={setFieldValue}
                  onBlur={handleBlur}
                  dataTestId="playerIdButtonBackgroundColor"
                  dataTestPopoverId="playerIdButtonBackgroundColorPopover"
                  dataTestColorOneId="playerIdButtonBackgroundColorOne"
                  dataTestColorTwoId="playerIdButtonBackgroundColorTwo"
                  dataTestGradientTypeId="playerIdButtonBackgroundColorGradientType"
                  dataTestGradientDirectionId="playerIdButtonBackgroundColorGradientDirection"
                  error={
                    touched.playerIdButton?.backgroundColor?.colorOne &&
                    Boolean(errors.playerIdButton?.backgroundColor?.colorOne)
                  }
                />
                <AcGradientInput
                  header="Button Text Color"
                  name="playerIdButton.textColor"
                  defaultValue={values.playerIdButton?.textColor}
                  setValue={setFieldValue}
                  onBlur={handleBlur}
                  dataTestId="playerIdButtonTextColor"
                  dataTestPopoverId="playerIdButtonTextColorPopover"
                  dataTestColorOneId="playerIdButtonTextColorOne"
                  dataTestColorTwoId="playerIdButtonTextColorTwo"
                  dataTestGradientTypeId="playerIdButtonTextColorGradientType"
                  dataTestGradientDirectionId="playerIdButtonTextColorGradientDirection"
                  error={
                    touched.playerIdButton?.textColor?.colorOne &&
                    Boolean(errors.playerIdButton?.textColor?.colorOne)
                  }
                />
              </div>
            </div>
          </AcCard>
          <Separator className="my-6 bg-base-border" />
        </Flex>
      </Grid>
      <Grid item xs={7} className="iphone-mock-wrapper">
        <div className="store-preview-wrapper iphone-mock">
          <Background
            backgroundImageMobile={
              values.loginBackgroundImageMobile ||
              generalTheme?.data?.backgroundImageMobile
            }
            backgroundImageDesktop={
              values.loginBackgroundImageDesktop ||
              generalTheme?.data?.backgroundImageDesktop
            }
            width="100%"
            height="100%"
            position="absolute"
          />
          <LoginPreview
            text={values.text}
            textColor={values.textColor}
            textSize={Number(values.textSize)}
            textWeight={values.textWeight}
            logo={values.loginLogoImage || generalTheme?.data?.logo || ''}
            loginLogoSize={values.loginLogoSize || 'small'}
            width="100%"
            height="100% - 200px"
            fontFamily={values.font}
            playerAuthData={{
              usernamePasswordOn: true,
              usernamePasswordModel: 'bcrypt',
              userTokenOn: false,
              userTokenModel: '',
              googleAppSecret: 'kfdKd92h7KJH&*^%@!$',
              googleAppId: '87391245034',
              googleModel: 'OAuth2',
              googleOn: true,
              appleAppSecret: 'JHF@&$#hfdk17d',
              appleAppId: '2734590234',
              appleModel: 'Sign in with Apple',
              appleOn: false,
              fbAppSecret: 'jfdl65s4sfgsdf4&^#',
              fbAppId: '958273405',
              fbModel: 'Facebook Login',
              fbOn: true,
              otpOn: getIntegration.data?.playersAuthentication?.otpOn || false,
              otpLinks: [],
              appleResponseType: 'code' as any,
              userTokenText: '',
              userTokenUrl: ''
            }}
            otpButton={{
              icon: values.otpButton.icon,
              text: values.otpButton.text,
              backgroundColor: values.otpButton.backgroundColor,
              textColor: values.otpButton.textColor
            }}
            facebookButtonVariant={values.facebookButton?.variant}
            htmlText={values.consentSection?.htmlText}
            consentRequired={!!values.consentSection?.consentRequired}
          />
        </div>
      </Grid>
    </Grid>
  );
};
